/**
 * LeadHero — top-of-page summary + quick actions for LeadDetailPage.
 *
 * Replaces the old inline header + Overview card with a single component that
 * the sales team can scan in five seconds:
 *   1. Identity row: name, status, signals, language/country/campaign chips.
 *   2. Meta row: assignee (editable select), created date, last activity.
 *   3. Next action banner (when present), with vencida warning.
 *   4. Quick actions: WhatsApp / Email / Llamar / Instagram / Editar contacto /
 *      Agendar reunión / Añadir villa hold / Convertir a inversor.
 *
 * The component is presentational over PATCH /leads/{id} (via the `patch`
 * callback) and over `/leads/{id}/notes` (via `onMarkActionDone`). It does
 * not own data or fetches.
 */
import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, EnvelopeSimple, Phone, WhatsappLogo, InstagramLogo,
  NotePencil, Calendar, House, UserPlus, CheckCircle,
  WarningOctagon, ClockCounterClockwise, Globe, Translate, Megaphone,
  User as UserIcon,
} from "@phosphor-icons/react";
import { SIGNAL_META, LEAD_STATUSES, normalizeStatus } from "@/lib/leadStatuses";
import { leadWhatsappHref } from "@/lib/bulkWhatsapp";

const LANGUAGE_LABEL = { es: "Español", en: "Inglés" };

function daysSince(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d)) return null;
  return Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24));
}

function fmtShortDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("es-ES", {
      day: "2-digit", month: "short", year: "numeric",
    });
  } catch { return iso; }
}

// Accepts handle, @handle, instagram.com/handle, https://… → returns canonical URL or null.
function instagramUrl(raw) {
  if (!raw) return null;
  let v = String(raw).trim();
  if (!v) return null;
  v = v.replace(/^https?:\/\/(www\.)?instagram\.com\//i, "");
  v = v.replace(/^@+/, "");
  v = v.split(/[/?#]/)[0];
  return v ? `https://instagram.com/${v}` : null;
}

function scrollToId(id) {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

export default function LeadHero({
  lead,
  assignees = [],
  busy = false,
  backLink,
  onPatch,
  onMarkActionDone,
  onRequestEditContact,
  onConvert,
  canConvert = false,
  lastActivityDays = null,
  nextActionLate = false,
}) {
  if (!lead) return null;

  const normalizedStatus = normalizeStatus(lead.status);
  const currentStatus = LEAD_STATUSES.find((s) => s.key === normalizedStatus);
  const signals = lead.signals || [];

  const phoneRaw = (lead.phone || "").trim();
  const emailRaw = (lead.email || "").trim();
  const igUrl = instagramUrl(lead.instagram);
  // Teléfono canónico + normalización canónica. El helper devuelve null si
  // el número no sirve, y entonces el botón queda deshabilitado en vez de
  // abrir un wa.me que falla al otro lado.
  const waUrl = leadWhatsappHref(lead);

  const createdDays = daysSince(lead.created_at);
  const languageLabel = lead.language ? (LANGUAGE_LABEL[lead.language] || lead.language) : null;

  // Status badge tone — reuses the existing dv-badge variants.
  const statusBadgeTone =
    normalizedStatus === "reserved" ? "dv-badge-baja"
    : normalizedStatus === "lost" ? "dv-badge-alta"
    : "dv-badge-media";

  return (
    <div className="mb-6 fade-up" data-testid="lead-hero">
      {/* Breadcrumb */}
      <Link
        to={backLink}
        className="text-xs tracking-[0.3em] uppercase text-stone-500 inline-flex items-center gap-1 mb-2 hover:text-stone-900"
        data-testid="lead-back"
      >
        <ArrowLeft size={12} /> CRM / Leads
      </Link>

      {/* Identity row */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <h1 className="font-serif-dv text-4xl sm:text-5xl leading-tight" data-testid="lead-name">
            {lead.name}
          </h1>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <span
              className={`dv-badge ${statusBadgeTone}`}
              data-testid="lead-status-badge"
            >
              {currentStatus?.label || "Nuevo"}
            </span>
            {signals.map((s) => {
              const meta = SIGNAL_META[s];
              if (!meta) return null;
              const tone =
                meta.tone === "red" ? "bg-red-100 text-red-800"
                : meta.tone === "amber" ? "bg-amber-100 text-amber-900"
                : "bg-stone-100 text-stone-600";
              return (
                <span
                  key={s}
                  data-testid={`lead-signal-${s}`}
                  className={`text-[11px] uppercase tracking-wider px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${tone}`}
                  title={meta.label}
                >
                  <WarningOctagon size={12} weight="bold" />
                  {meta.label}
                </span>
              );
            })}
            {lead.country && (
              <Chip icon={Globe} testid="lead-hero-country">{lead.country}</Chip>
            )}
            {languageLabel && (
              <Chip icon={Translate} testid="lead-hero-language">{languageLabel}</Chip>
            )}
            {lead.campaign && (
              <Chip icon={Megaphone} testid="lead-campaign">{lead.campaign}</Chip>
            )}
            {lead.timeline && (
              <Chip icon={Calendar} testid="lead-timeline">{lead.timeline}</Chip>
            )}
          </div>
          <StageReason lead={lead} />
        </div>

        {/* Convert CTA — kept prominent on the right when applicable. */}
        {canConvert && (
          <button
            type="button"
            onClick={onConvert}
            className="dv-btn text-white shrink-0"
            style={{ background: "#2F3E35" }}
            data-testid="convert-btn"
          >
            <UserPlus size={14} /> Convertir a inversor
          </button>
        )}
      </div>

      {/* Meta row: assignee + created + last activity */}
      <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-stone-600">
        <label className="inline-flex items-center gap-1.5">
          <UserIcon size={12} className="text-stone-400" />
          <span className="text-stone-400 uppercase tracking-widest text-[10px]">Responsable</span>
          <select
            value={lead.assigned_to_user_id || ""}
            onChange={(e) => onPatch?.({ assigned_to_user_id: e.target.value || null })}
            className="text-xs border border-stone-200 rounded px-2 py-1 bg-white"
            disabled={busy}
            data-testid="lead-assignee"
          >
            <option value="">Sin responsable</option>
            {assignees.map((u) => (
              <option key={u.user_id} value={u.user_id}>
                {u.name} · {u.role}
              </option>
            ))}
          </select>
        </label>
        <span className="inline-flex items-center gap-1">
          <Calendar size={12} className="text-stone-400" />
          <span className="text-stone-400 uppercase tracking-widest text-[10px]">Creado</span>
          <span>{fmtShortDate(lead.created_at)}{createdDays != null && ` · hace ${createdDays}d`}</span>
        </span>
        {lastActivityDays !== null && (
          <span className="inline-flex items-center gap-1">
            <ClockCounterClockwise size={12} className="text-stone-400" />
            {lastActivityDays === 0 ? "Actividad hoy" : `Última actividad hace ${lastActivityDays}d`}
          </span>
        )}
      </div>

      {/* Next action banner */}
      {lead.next_action && (
        <div
          className={`mt-4 rounded-md border px-3 py-2 flex items-start justify-between gap-3 ${
            nextActionLate
              ? "bg-red-50 border-red-200"
              : "bg-stone-50 border-stone-200"
          }`}
          data-testid="lead-hero-next-action"
        >
          <div className="text-sm min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-stone-500">
              Próxima acción {nextActionLate && (
                <span className="ml-1 text-red-700 font-semibold">· VENCIDA</span>
              )}
            </div>
            <div className="text-stone-800 mt-0.5 break-words">
              {lead.next_action}
              {lead.next_action_date && (
                <span className="text-stone-500 ml-2">· {lead.next_action_date}{lead.next_action_time ? ` ${lead.next_action_time}` : ""}</span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={() => scrollToId("section-next-action")}
              className="text-xs text-stone-600 hover:text-stone-900 inline-flex items-center gap-1"
              data-testid="lead-hero-next-action-edit"
            >
              <NotePencil size={12} /> Editar
            </button>
            <button
              type="button"
              onClick={onMarkActionDone}
              disabled={busy}
              className="text-xs text-stone-700 hover:text-emerald-700 inline-flex items-center gap-1"
              data-testid="lead-hero-next-action-done"
            >
              <CheckCircle size={12} weight="bold" /> Hecha
            </button>
          </div>
        </div>
      )}

      {/* Quick actions — horizontal scroll on mobile, wrap on desktop. */}
      <div
        className="mt-4 flex sm:flex-wrap items-center gap-2 overflow-x-auto pb-1 -mx-1 px-1"
        data-testid="lead-hero-quick-actions"
      >
        <QuickAction
          icon={WhatsappLogo}
          label="WhatsApp"
          href={waUrl}
          external
          disabled={!waUrl}
          disabledTitle={waUrl ? undefined : "Sin WhatsApp válido — añade un número con código país"}
          testid="lead-hero-wa"
        />
        <QuickAction
          icon={EnvelopeSimple}
          label="Email"
          href={emailRaw ? `mailto:${emailRaw}` : null}
          disabled={!emailRaw}
          disabledTitle="Sin email"
          testid="lead-hero-email"
        />
        <QuickAction
          icon={Phone}
          label="Llamar"
          href={phoneRaw ? `tel:${phoneRaw}` : null}
          disabled={!phoneRaw}
          disabledTitle="Sin teléfono"
          testid="lead-hero-call"
        />
        <QuickAction
          icon={InstagramLogo}
          label="Instagram"
          href={igUrl}
          external
          disabled={!igUrl}
          disabledTitle="Sin Instagram"
          testid="lead-hero-instagram"
        />
        <QuickAction
          icon={NotePencil}
          label="Editar contacto"
          onClick={() => {
            scrollToId("section-contact");
            onRequestEditContact?.();
          }}
          testid="lead-hero-edit-contact"
        />
        <QuickAction
          icon={Calendar}
          label="Agendar reunión"
          onClick={() => scrollToId("section-meeting")}
          testid="lead-hero-schedule-meeting"
        />
        <QuickAction
          icon={House}
          label="Villa en hold"
          onClick={() => scrollToId("section-villa-hold")}
          testid="lead-hero-villa-hold"
        />
      </div>
    </div>
  );
}

// Por qué el lead está en esta fase. Una línea, debajo de la etapa, en el
// sitio donde ya se mira el estado: la alternativa era otra tarjeta, y la
// ficha del lead ya tiene las que necesita.
//
// `stage_source` lo escribe `lead_lifecycle`. Los leads anteriores a esa PR
// no lo tienen y aquí no se pinta nada — decir "Manual" por defecto sería
// inventar una procedencia que nadie registró.
const STAGE_SOURCE_LABEL = {
  user: "Manual",
  automation: "Automatización",
  system: "Sistema",
};

const STAGE_REASON_LABEL = {
  first_outbound_confirmed: "le escribimos y el envío se confirmó",
  lead_replied: "el lead respondió",
  reactivated_by_lead_reply: "un lead perdido volvió a escribir",
  explicit_qualification_evidence: "dijo explícitamente qué quiere, cuándo y con qué presupuesto",
};

function StageReason({ lead }) {
  const source = STAGE_SOURCE_LABEL[lead.stage_source];
  if (!source) return null;
  const reason = STAGE_REASON_LABEL[lead.stage_reason];
  const when = fmtShortDate(lead.stage_changed_at || lead.stage_entered_at);
  return (
    <div
      className="mt-1.5 text-[11px] text-stone-500 dark:text-stone-400 break-words"
      data-testid="lead-stage-reason"
    >
      {source}
      {reason ? ` · ${reason}` : ""}
      {when ? ` · ${when}` : ""}
    </div>
  );
}

function Chip({ icon: Icon, children, testid }) {
  return (
    <span
      className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border bg-stone-50 text-stone-700 border-stone-200"
      data-testid={testid}
    >
      {Icon ? <Icon size={11} /> : null}
      {children}
    </span>
  );
}

function QuickAction({
  icon: Icon, label, href, onClick, external = false,
  disabled = false, disabledTitle, testid,
}) {
  const base =
    "shrink-0 inline-flex items-center justify-center gap-1.5 text-xs px-3 "
    + "min-h-[44px] sm:min-h-0 sm:py-1.5 rounded-full border transition whitespace-nowrap";
  const styles = disabled
    ? "bg-stone-50 text-stone-400 border-stone-200 cursor-not-allowed"
    : "bg-white text-stone-800 border-stone-300 hover:border-stone-900 hover:text-stone-900";

  if (disabled || !href) {
    return (
      <button
        type="button"
        onClick={disabled ? undefined : onClick}
        disabled={disabled}
        title={disabled ? disabledTitle : undefined}
        className={`${base} ${styles}`}
        data-testid={testid}
      >
        <Icon size={14} />
        {label}
      </button>
    );
  }
  return (
    <a
      href={href}
      target={external ? "_blank" : undefined}
      rel={external ? "noopener noreferrer" : undefined}
      className={`${base} ${styles}`}
      data-testid={testid}
    >
      <Icon size={14} />
      {label}
    </a>
  );
}
